import apiClient from './client';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface FieldMapping {
  /** Column ID mapped to "Scheduled Hours (Total)" concept */
  scheduledHoursColumnId: string | null;
  /** Column ID mapped to "Scheduled Hours Per Day" concept (Numbers column) */
  scheduledHoursPerDayColumnId: string | null;
  /** Column ID mapped to "Assignees" concept (People column) */
  assigneesColumnId: string | null;
  /** Column ID mapped to "Timeline" concept (Timeline column) */
  timelineColumnId: string | null;
  /** Column ID mapped to "Status" concept */
  statusColumnId: string | null;
  /** Column ID mapped to "Color" concept (Color Picker column) */
  colorColumnId: string | null;
}

export type ItemLevel = 'main' | 'sub' | 'both';
export type ColorSchema = 'picker' | 'group' | 'status';
export type ItemHeightMode = 'compact' | 'comfortable' | 'expanded';
export type CapacityMode = 'manual' | 'scheme';
export type WorkHourFormat = 'per_day' | 'total';
export interface BoardConfig {
  boardId: number;
  fieldMapping: FieldMapping;
  /** Field mapping for sub-items (columns on the subitems board) */
  subitemFieldMapping: FieldMapping;
  /** Which item levels to show */
  itemLevel: ItemLevel;
  /** How to determine item colors */
  colorSchema: ColorSchema;
  /** Row height mode */
  itemHeightMode: ItemHeightMode;
  /** Capacity calculation mode */
  capacityMode: CapacityMode;
  /** How work hours are displayed: per day or total for the task duration. null = use account default */
  workHourFormat: WorkHourFormat | null;
  /** Whether to divide work hours equally among assignees for workload calculations. null = use account default */
  perPersonDistribution: boolean | null;
  /** Whether to show parent item name on subitem events */
  showParentItemName: boolean;
  /** Whether to exclude completed items from the scheduler */
  excludeCompleted: boolean;
  /** Default worklog category for new time entries on this board (null = use system default) */
  defaultWorklogCategoryId: string | null;
  createdAt: string;
  updatedAt: string;
}

export type UpdateBoardConfigPayload = Partial<
  Omit<BoardConfig, 'boardId' | 'createdAt' | 'updatedAt'>
>;

/** A FieldMapping with every column unset. Useful as a safe fallback when a
 *  board uses sub-only configuration and the main mapping is intentionally
 *  blank. */
export const EMPTY_FIELD_MAPPING: FieldMapping = {
  timelineColumnId: null,
  assigneesColumnId: null,
  scheduledHoursColumnId: null,
  scheduledHoursPerDayColumnId: null,
  statusColumnId: null,
  colorColumnId: null,
};

/**
 * Whether a board's field mapping is complete enough to use the scheduler.
 * Required columns depend on `itemLevel` — a board configured for sub-items
 * only doesn't need the main mapping populated, and vice versa.
 */
export function isBoardConfigured(config: {
  fieldMapping: FieldMapping | null | undefined;
  subitemFieldMapping: FieldMapping | null | undefined;
  itemLevel: ItemLevel;
}): boolean {
  const mainOK = !!(
    config.fieldMapping?.timelineColumnId && config.fieldMapping?.assigneesColumnId
  );
  const subOK = !!(
    config.subitemFieldMapping?.timelineColumnId &&
    config.subitemFieldMapping?.assigneesColumnId
  );
  switch (config.itemLevel) {
    case 'sub':
      return subOK;
    case 'both':
      return mainOK && subOK;
    case 'main':
    default:
      return mainOK;
  }
}

/** Represents a monday.com column available for field mapping. */
export interface MondayColumn {
  id: string;
  title: string;
  type: string;
}

/** Paginated response from the list endpoint. */
export interface BoardConfigListResponse {
  items: BoardConfig[];
  total: number;
  page: number;
  pageSize: number;
}

// ─── In-memory cache for synchronous access ──────────────────────────────────

const fieldMappingCache = new Map<number, FieldMapping>();
const subitemFieldMappingCache = new Map<number, FieldMapping>();
const itemLevelCache = new Map<number, ItemLevel>();
const colorSchemaCache = new Map<number, ColorSchema>();
/** Per-board perPersonDistribution cache. null = use account default (not yet resolved). */
const perPersonDistributionCache = new Map<number, boolean | null>();
const showParentItemNameCache = new Map<number, boolean>();

// ─── Backend API operations ──────────────────────────────────────────────────

/**
 * List all board configurations for the current account, with pagination.
 */
export async function listBoardConfigs(
  page = 1,
  pageSize = 10,
): Promise<BoardConfigListResponse> {
  const { data } = await apiClient.get<BoardConfigListResponse>('/board-config', {
    params: { page, pageSize },
  });
  return data;
}

/**
 * Delete a board configuration.
 */
export async function deleteBoardConfig(boardId: number): Promise<void> {
  await apiClient.delete(`/board-config/${boardId}`);
  _boardConfigCache.delete(boardId);
  fieldMappingCache.delete(boardId);
  subitemFieldMappingCache.delete(boardId);
  itemLevelCache.delete(boardId);
  colorSchemaCache.delete(boardId);
  perPersonDistributionCache.delete(boardId);
  showParentItemNameCache.delete(boardId);
}

/** In-flight dedup + short cache for getBoardConfig */
const _boardConfigCache = new Map<number, { data: BoardConfig; timestamp: number }>();
const _boardConfigPromises = new Map<number, Promise<BoardConfig>>();
const BOARD_CONFIG_CACHE_TTL_MS = 60_000; // 1 minute

/** Populate all per-board caches from a BoardConfig. */
function warmCacheEntry(data: BoardConfig): void {
  const boardId = data.boardId;
  _boardConfigCache.set(boardId, { data, timestamp: Date.now() });
  if (data.fieldMapping) fieldMappingCache.set(boardId, data.fieldMapping);
  if (data.subitemFieldMapping) subitemFieldMappingCache.set(boardId, data.subitemFieldMapping);
  if (data.itemLevel) itemLevelCache.set(boardId, data.itemLevel);
  if (data.colorSchema) colorSchemaCache.set(boardId, data.colorSchema);
  perPersonDistributionCache.set(boardId, data.perPersonDistribution);
  showParentItemNameCache.set(boardId, data.showParentItemName ?? false);
}

/**
 * Fetch configs for multiple boards in a single POST request.
 * Populates all per-board caches so subsequent getBoardConfig calls are instant.
 * Returns a Map<boardId, BoardConfig> for easy lookup.
 */
export async function getBoardConfigsBulk(
  boardIds: number[],
): Promise<Map<number, BoardConfig>> {
  if (boardIds.length === 0) return new Map();

  // Filter out boards already in cache
  const now = Date.now();
  const uncachedIds: number[] = [];
  const result = new Map<number, BoardConfig>();

  for (const id of boardIds) {
    const cached = _boardConfigCache.get(id);
    if (cached && now - cached.timestamp < BOARD_CONFIG_CACHE_TTL_MS) {
      result.set(id, cached.data);
    } else {
      uncachedIds.push(id);
    }
  }

  if (uncachedIds.length > 0) {
    const { data } = await apiClient.post<{ items: BoardConfig[] }>('/board-config/bulk', {
      boardIds: uncachedIds.map(String),
    });
    for (const config of data.items) {
      warmCacheEntry(config);
      result.set(config.boardId, config);
    }
  }

  return result;
}

/**
 * Get the board configuration from the backend.
 * Deduplicates concurrent calls and caches for 1 minute.
 * Also updates the local field mapping cache for synchronous access.
 */
export async function getBoardConfig(boardId: number): Promise<BoardConfig> {
  // Return cache if valid
  const cached = _boardConfigCache.get(boardId);
  if (cached && Date.now() - cached.timestamp < BOARD_CONFIG_CACHE_TTL_MS) {
    return cached.data;
  }
  // Deduplicate concurrent calls for the same board
  const inflight = _boardConfigPromises.get(boardId);
  if (inflight) return inflight;

  const promise = apiClient.get<BoardConfig>(`/board-config/${boardId}`).then((res) => res.data);
  _boardConfigPromises.set(boardId, promise);

  let data: BoardConfig;
  try {
    data = await promise;
    warmCacheEntry(data);
  } finally {
    _boardConfigPromises.delete(boardId);
  }

  return data;
}

/**
 * Update the board configuration on the backend (partial update).
 * Also updates the local field mapping cache.
 */
export async function updateBoardConfig(
  boardId: number,
  payload: UpdateBoardConfigPayload,
): Promise<BoardConfig> {
  const { data } = await apiClient.put<BoardConfig>(`/board-config/${boardId}`, payload);
  // Invalidate the dedup cache so next getBoardConfig fetches fresh
  _boardConfigCache.delete(boardId);
  // Update caches
  if (data.fieldMapping) {
    fieldMappingCache.set(boardId, data.fieldMapping);
  }
  if (data.subitemFieldMapping) {
    subitemFieldMappingCache.set(boardId, data.subitemFieldMapping);
  }
  if (data.itemLevel) {
    itemLevelCache.set(boardId, data.itemLevel);
  }
  if (data.colorSchema) {
    colorSchemaCache.set(boardId, data.colorSchema);
  }
  perPersonDistributionCache.set(boardId, data.perPersonDistribution);
  showParentItemNameCache.set(boardId, data.showParentItemName ?? false);

  return data;
}

/**
 * Get the field mapping from the in-memory cache (synchronous).
 * The cache is populated by getBoardConfig / updateBoardConfig calls.
 * Returns null if the config hasn't been fetched yet.
 */
export function getFieldMappingLocal(boardId: number): FieldMapping | null {
  return fieldMappingCache.get(boardId) ?? null;
}

/**
 * Get the subitem field mapping from the in-memory cache (synchronous).
 */
export function getSubitemFieldMappingLocal(boardId: number): FieldMapping | null {
  return subitemFieldMappingCache.get(boardId) ?? null;
}

/**
 * Get the item level setting from the in-memory cache (synchronous).
 */
export function getItemLevelLocal(boardId: number): ItemLevel {
  return itemLevelCache.get(boardId) ?? 'main';
}

/**
 * Get the color schema setting from the in-memory cache (synchronous).
 */
export function getColorSchemaLocal(boardId: number): ColorSchema {
  return colorSchemaCache.get(boardId) ?? 'group';
}

/**
 * Get the per-person distribution setting from the in-memory cache (synchronous).
 * Returns null if set to "use account default" or if the config hasn't been fetched yet.
 */
export function getPerPersonDistributionLocal(boardId: number): boolean | null {
  return perPersonDistributionCache.get(boardId) ?? null;
}

/**
 * Get the showParentItemName setting from the in-memory cache (synchronous).
 */
export function getShowParentItemNameLocal(boardId: number): boolean {
  return showParentItemNameCache.get(boardId) ?? false;
}

/**
 * Update the field mapping for a board on the backend.
 * Also updates the local cache.
 */
export async function updateFieldMapping(
  boardId: number,
  mapping: FieldMapping,
): Promise<FieldMapping> {
  await updateBoardConfig(boardId, { fieldMapping: mapping });
  return mapping;
}

/**
 * Get available monday.com columns for a board's subitems board.
 * Resolves the subitems board ID from the parent board's "subtasks" column,
 * then fetches columns from that board.
 */
export async function getSubitemsBoardColumns(
  parentBoardId: number,
): Promise<{ subitemsBoardId: number | null; columns: MondayColumn[] }> {
  const { fetchSubitemsBoardInfo } = await import('./monday.api');
  const info = await fetchSubitemsBoardInfo(parentBoardId);
  if (!info) return { subitemsBoardId: null, columns: [] };
  const columns = await getBoardColumns(info.subitemsBoardId);
  return { subitemsBoardId: info.subitemsBoardId, columns };
}

/**
 * Get available monday.com columns for a board (for field mapping dropdowns).
 * Fetches directly from monday.com GraphQL API via the SDK.
 */
export async function getBoardColumns(
  boardId: number,
): Promise<MondayColumn[]> {
  const mondaySdk = await import('monday-sdk-js');
  const monday = mondaySdk.default();

  const query = `query GetBoardColumns($boardId: [ID!]!) {
    boards(ids: $boardId) {
      columns { id title type }
    }
  }`;

  const response = await monday.api(query, {
    variables: { boardId: [String(boardId)] },
  });

  const data = response.data as {
    boards?: Array<{ columns: Array<{ id: string; title: string; type: string }> }>;
  };

  const board = data.boards?.[0];
  if (!board) return [];

  return board.columns.map((col) => ({
    id: col.id,
    title: col.title,
    type: col.type,
  }));
}
